import { Link } from 'react-router';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, CloudOff, RefreshCw, Smartphone, Wifi } from 'lucide-react';
import { useSEO } from '../lib/seo';

export default function HelpSync() {
  useSEO({
    title: 'Sync & personalization help | TENSION',
    description:
      'Fix offline sync, adaptive personalization, and Relationship Intelligence data on web and the TENSION app.',
    canonical: '/help/sync',
  });

  return (
    <div className="min-h-screen tension-page-bg">
      <div className="mx-auto max-w-2xl px-4 py-8 sm:py-12">
        <Button variant="ghost" size="sm" className="mb-6 gap-2" asChild>
          <Link to="/settings">
            <ArrowLeft className="h-4 w-4" aria-hidden />
            Back to Settings
          </Link>
        </Button>

        <h1 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100">Sync & personalization</h1>
        <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
          How TENSION keeps your adaptive experience reliable on the web and in the Capacitor app (iOS / Android).
        </p>

        <div className="mt-8 space-y-4">
          <Card className="p-5">
            <div className="flex gap-3">
              <Wifi className="h-5 w-5 shrink-0 text-violet-600" aria-hidden />
              <div>
                <h2 className="font-medium text-neutral-900 dark:text-neutral-100">Connection</h2>
                <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
                  Personalization signals sync when you are online. If you were offline, open the app again on Wi‑Fi or
                  cellular — we retry automatically when the connection returns or when you bring the app to the
                  foreground.
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-5">
            <div className="flex gap-3">
              <CloudOff className="h-5 w-5 shrink-0 text-amber-600" aria-hidden />
              <div>
                <h2 className="font-medium text-neutral-900 dark:text-neutral-100">“Sync paused”</h2>
                <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
                  You may see this when the network fails or the server is busy. Your device still stores signals locally
                  and merges them when sync succeeds. Pull to refresh on home, or sign out and back in if it persists
                  for more than a day.
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-5">
            <div className="flex gap-3">
              <Smartphone className="h-5 w-5 shrink-0 text-fuchsia-600" aria-hidden />
              <div>
                <h2 className="font-medium text-neutral-900 dark:text-neutral-100">iOS & Android (Capacitor)</h2>
                <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
                  The native shell uses the same web app. Allow network access for TENSION in system settings. After an
                  OS update, open the app once while online so queued personalization events can upload.
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-5">
            <div className="flex gap-3">
              <RefreshCw className="h-5 w-5 shrink-0 text-green-600" aria-hidden />
              <div>
                <h2 className="font-medium text-neutral-900 dark:text-neutral-100">Clear & reset</h2>
                <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
                  In Settings → Adaptive personalization, you can clear server and on-device signal history. That does
                  not delete check-ins or journal entries. For account deletion, use the account tools in Settings.
                </p>
              </div>
            </div>
          </Card>
        </div>

        <p className="mt-8 text-xs text-neutral-500">
          Need more help?{' '}
          <Link to="/support" className="text-violet-600 underline-offset-2 hover:underline">
            Contact support
          </Link>
          .
        </p>
      </div>
    </div>
  );
}
